/**
 * Owner: Masa Bayu
 * File: qxp-artifact-figures.ts
 * Date:Time: 2026-10-30 10:05
 * Description: Resolve QXP pageFigures + assetRef per visible page — no PDF.
 */

import type { FetchLike, QxpFetchInit } from './qxp-artifact-registry';

async function defaultFetch(url: string, init?: QxpFetchInit) {
  const res = await fetch(url, init);
  const raw = await res.text();
  return {
    ok: res.ok,
    status: res.status,
    json: async () => JSON.parse(raw) as unknown,
    text: async () => raw,
  };
}

export type QxpPageFigure = {
  readonly elementId: string;
  readonly pageNumber: number;
  readonly src: string;
  readonly assetRef: string;
  readonly layout: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly rotation: number;
    readonly zIndex: number;
  } | null;
};

const FIGURE_PAGE = /(?:^|\/)figure-el_p(\d+)_/;
const pageCache = new Map<string, Map<number, readonly QxpPageFigure[]>>();

export function clearFigureCache(): void {
  pageCache.clear();
}

export function getCachedPageFigures(
  publicationId: string,
  pageNumber: number,
): readonly QxpPageFigure[] {
  return pageCache.get(publicationId.trim())?.get(pageNumber) ?? [];
}

export async function ensurePageFigures(
  publicationId: string,
  pageNumber: number,
  base: string,
  paths: readonly string[],
  headers: Record<string, string>,
  fetchImpl: FetchLike = defaultFetch,
): Promise<readonly QxpPageFigure[]> {
  const id = publicationId.trim();
  const existing = pageCache.get(id)?.get(pageNumber);
  if (existing !== undefined) return existing;
  const loaded = await loadFiguresForPage(base, paths, headers, fetchImpl);
  let byPage = pageCache.get(id);
  if (byPage === undefined) {
    byPage = new Map();
    pageCache.set(id, byPage);
  }
  byPage.set(pageNumber, loaded);
  return loaded;
}

export function figurePathsByPageFromManifest(
  pageFigures: unknown,
): ReadonlyMap<number, readonly string[]> {
  const grouped = new Map<number, string[]>();
  if (!Array.isArray(pageFigures)) return grouped;
  for (const path of pageFigures) {
    if (typeof path !== 'string' || !path.startsWith('content/figure-')) continue;
    if (path.includes('..')) continue;
    const match = FIGURE_PAGE.exec(path);
    if (match === null) continue;
    const page = Number.parseInt(match[1] ?? '', 10);
    if (!Number.isInteger(page) || page < 1) continue;
    const list = grouped.get(page) ?? [];
    list.push(path);
    grouped.set(page, list);
  }
  return grouped;
}

async function readBody(res: { json: () => Promise<unknown>; text?: () => Promise<string> }): Promise<unknown> {
  if (typeof res.text === 'function') {
    const raw = await res.text();
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return raw;
    }
  }
  return res.json();
}

function layoutOf(raw: unknown): QxpPageFigure['layout'] {
  if (raw === null || typeof raw !== 'object') return null;
  const layout = raw as Record<string, unknown>;
  const x = layout.x;
  const y = layout.y;
  const width = layout.width;
  const height = layout.height;
  if (
    typeof x !== 'number' ||
    typeof y !== 'number' ||
    typeof width !== 'number' ||
    typeof height !== 'number'
  ) {
    return null;
  }
  return {
    x,
    y,
    width,
    height,
    rotation: typeof layout.rotation === 'number' ? layout.rotation : 0,
    zIndex: typeof layout.zIndex === 'number' ? layout.zIndex : 0,
  };
}

export async function loadFiguresForPage(
  base: string,
  paths: readonly string[],
  headers: Record<string, string>,
  fetchImpl: FetchLike,
): Promise<readonly QxpPageFigure[]> {
  const out: QxpPageFigure[] = [];
  const assetCache = new Map<string, string>();
  for (const path of paths) {
    const res = await fetchImpl(`${base}/${path}`, { headers });
    if (!res.ok) {
      const status = res.status ?? 500;
      if (status === 401 || status === 403) continue;
      continue;
    }
    const file = (await readBody(res)) as Record<string, unknown>;
    if (file.kind !== 'image') continue;
    const assetRef = typeof file.assetRef === 'string' ? file.assetRef : '';
    let src = typeof file.src === 'string' && file.src.startsWith('data:') ? file.src : '';
    if (src.length === 0 && assetRef.startsWith('assets/')) {
      const cached = assetCache.get(assetRef);
      if (cached !== undefined) {
        src = cached;
      } else {
        const assetRes = await fetchImpl(`${base}/${assetRef}`, { headers });
        if (!assetRes.ok) continue;
        const body = await readBody(assetRes);
        if (typeof body === 'string' && body.startsWith('data:')) {
          src = body;
          assetCache.set(assetRef, src);
        }
      }
    }
    if (src.length === 0) continue;
    const page = file.page;
    const pageNumber =
      page !== null && typeof page === 'object' && typeof (page as { number?: unknown }).number === 'number'
        ? (page as { number: number }).number
        : Number.parseInt(FIGURE_PAGE.exec(path)?.[1] ?? '', 10);
    if (!Number.isInteger(pageNumber) || pageNumber < 1) continue;
    out.push({
      elementId: typeof file.elementId === 'string' ? file.elementId : path,
      pageNumber,
      src,
      assetRef,
      layout: layoutOf(file.layout),
    });
  }
  return out;
}
